import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, MenuItem, Collapse } from '@material-ui/core';
import Alert from './common/Alert';
import Input from './common/Input';
import Select from './common/Select';
import Icon from './common/Icon';
import { useStores } from '../stores';
import PhoneInput from './PhoneInput';
import appStyles from '../../../config/appStyles.js';
import { ValidatorForm } from 'react-material-ui-form-validator';
import Button from './common/Button';
import LinkButton from './common/LinkButton';
import { useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  ...appStyles.profileInfo(theme),
  missingField: {
    '& .MuiInputLabel-filled': {
      color: '#396DCF !important',
    },
    '& .MuiFilledInput-input': {
      color: '#396DCF !important',
    },
  },
}));

const ProfileInfo = (props) => {
  const classes = useStyles();
  const location = useLocation();
  const { t } = useTranslation(['common', 'components', 'validations']);
  const { userStore } = useStores();
  const { user } = userStore;
  const [userInfo, setUserInfo] = useState(user);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [password, setPassword] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalIcon, setModalIcon] = useState();
  const [isPassVisible, setIsPassVisible] = useState(false);
  const [showActualPass, setShowActualPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmationPass, setConfirmationPass] = useState(false);
  const [dateInputValue, setDateInputValue] = useState(
    userInfo.birthYear && userInfo.birthMonth && userInfo.birthDay
      ? `${userInfo.birthYear}-${String(userInfo.birthMonth).padStart(
          2,
          '0',
        )}-${String(userInfo.birthDay).padStart(2, '0')}`
      : '',
  );
  const [birthdayError, setBirthdayError] = useState('');

  const requiredFields = [
    'name',
    'lastName',
    'phone',
    'gender',
    'birthDay',
    'birthYear',
    'birthMonth',
  ];

  const isMissingField = (fieldName) => {
    return !userInfo[fieldName] && requiredFields.includes(fieldName);
  };

  const updateField = (e) => {
    setUserInfo({
      ...userInfo,
      [e.target.name]: e.target.value,
    });
  };

  const confirmPasswordError = useMemo(
    () => newPassword && newPassword !== confirmPassword,
    [newPassword, confirmPassword],
  );
  const insertNewPasswordError = useMemo(
    () => !newPassword && password,
    [newPassword, password],
  );
  const passwordRequired = useMemo(
    () => newPassword !== '' && newPassword !== undefined && !password,
    [newPassword, password],
  );

  const handleSubmit = (evt) => {
    evt.preventDefault();
    setIsPassVisible(false);
    const updatedUserInfo = { ...userInfo, newPassword, password };
    updateUserData(updatedUserInfo)
      .then(() => {
        if (window.snakeAnalytics) {
          window.snakeAnalytics.push([
            'event',
            'im_edit_profile',
            {
              eventData: {
                im_content: 'profile',
                im_interaction: 'save_changes',
              },
            },
          ]);
          if (newPassword) {
            window.snakeAnalytics.push([
              'event',
              'im_password_update',
              {
                eventData: {
                  im_location: location.pathname + location.search,
                },
              },
            ]);
          }
        }
      })
      .catch((err) => {
        if (window.snakeAnalytics) {
          window.snakeAnalytics.push([
            'event',
            'im_error',
            {
              eventData: {
                im_message: err,
                im_location: location.pathname + location.search,
              },
            },
          ]);
        }
      });
  };

  const updateUserData = (userData) => {
    return new Promise((resolve, reject) => {
      userStore.updateUserData(userData, (isSuccess) => {
        if (isSuccess) {
          resolve();
        } else {
          reject(new Error('Update profile failed'));
        }
      });
    });
  };

  const handleCancel = () => {
    setConfirmPassword('');
    setPassword('');
    setNewPassword('');
    setIsPassVisible(false);
    setUserInfo({ ...user });
    if (window.snakeAnalytics) {
      window.snakeAnalytics.push([
        'event',
        'im_edit_profile',
        {
          eventData: {
            im_content: 'profile',
            im_interaction: 'cancel',
          },
        },
      ]);
    }
  };

  const handleBirthdayChange = (e) => {
    const dateValue = e.target.value;
    setDateInputValue(dateValue);

    const [year, month, day] = dateValue.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day);
    const today = new Date();

    if (!year || !month || !day) {
      setBirthdayError(
        t('validations:requiredField', {
          name: t('components:ProfileInfo.birthday'),
        }),
      );
      return;
    }

    if (year < 1900 || selectedDate > today) {
      setBirthdayError(
        t('validations:requiredField', {
          name: t('components:ProfileInfo.birthday'),
        }),
      );
      return;
    }

    setBirthdayError('');
    setUserInfo((prevUserInfo) => ({
      ...prevUserInfo,
      birthYear: year,
      birthMonth: month,
      birthDay: day,
    }));
  };

  return (
    <div id={props.id + '-profile-info-container'} className={classes.root}>
      <ValidatorForm onSubmit={handleSubmit}>
        <Grid container className={classes.containerProf}>
          <Grid container spacing={2}>
            <Grid xs={12} md={6} className={classes.profileInfoColumn}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Input
                    className={`${classes.inputProf} ${
                      isMissingField('name') ? classes.missingField : ''
                    }`}
                    validators={['required']}
                    errorMessages={[t('validations:requiredField')]}
                    label={t('components:ProfileInfo.name')}
                    name={'name'}
                    value={userInfo.name}
                    onChange={updateField}
                    type={'text'}
                    autoComplete="given-name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Input
                    className={`${classes.inputProf} ${
                      isMissingField('lastName') ? classes.missingField : ''
                    }`}
                    validators={['required']}
                    errorMessages={[t('validations:requiredField')]}
                    label={t('components:ProfileInfo.lastName')}
                    name={'lastName'}
                    value={userInfo.lastName}
                    onChange={updateField}
                    type={'text'}
                    autoComplete="family-name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <PhoneInput
                    className={
                      isMissingField('phone') ? classes.missingField : ''
                    }
                    label={t('components:ProfileInfo.phone')}
                    validators={['required', 'isTelephoneValid']}
                    value={userInfo.phone}
                    errorMessages={[
                      t('validations:requiredField'),
                      t('validations:phoneIsNotValid'),
                    ]}
                    onChange={updateField}
                    autoComplete="tel"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Input
                    className={classes.inputProf}
                    label={t('components:ProfileInfo.email')}
                    name="email"
                    value={userInfo.email}
                    type="email"
                    disabled
                    autoComplete="email"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={12} md={6} className={classes.profileInfoColumn}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Select
                    className={`${classes.indexSelect} ${
                      isMissingField('gender') ? classes.missingField : ''
                    }`}
                    label={t('components:ProfileInfo.gender')}
                    name="gender"
                    value={userInfo.gender || ''}
                    onChange={(e) => {
                      setUserInfo({
                        ...userInfo,
                        gender: e.target.value,
                      });
                    }}>
                    {Object.entries(
                      t('components:ProfileInfo.genders', {
                        returnObjects: true,
                      }),
                    ).map(([key, label]) => {
                      const genderMap = {
                        male: 'm',
                        female: 'f',
                        unknown: 'u',
                      };
                      return (
                        <MenuItem key={key} value={genderMap[key]}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <Input
                    className={`${classes.dateInput} ${
                      birthdayError ? classes.missingField : ''
                    }`}
                    label={t('components:ProfileInfo.birthday')}
                    name="birthday"
                    value={dateInputValue}
                    onChange={handleBirthdayChange}
                    type="date"
                    error={Boolean(birthdayError)}
                    helperText={birthdayError}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  {!isPassVisible ? (
                    <Input
                      className={classes.inputProf}
                      label={t('components:ProfileInfo.password')}
                      name={'password_placeholder'}
                      value="********"
                      InputProps={{
                        endAdornment: (
                          <div
                            style={{ cursor: 'pointer' }}
                            onClick={() => setShowActualPass((prev) => !prev)}>
                            {isPassVisible && (
                              <>
                                {showActualPass ? (
                                  <Icon
                                    className={classes.iconPass}
                                    name="icon-password"
                                  />
                                ) : (
                                  <Icon
                                    className={classes.iconPass}
                                    name="icon-password-disabled"
                                  />
                                )}
                              </>
                            )}
                          </div>
                        ),
                      }}
                      type={showActualPass ? 'text' : 'password'}
                    />
                  ) : (
                    <Input
                      className={classes.inputProf}
                      id={'id-currentPassword'}
                      validators={password ? ['required'] : []}
                      errorMessages={[t('validations:requiredField')]}
                      label={t('components:ProfileInfo.currentPassword')}
                      name={'password'}
                      value={password}
                      error={passwordRequired}
                      InputProps={{
                        endAdornment: (
                          <div
                            style={{ cursor: 'pointer' }}
                            onClick={() => setShowActualPass((prev) => !prev)}>
                            {showActualPass ? (
                              <Icon
                                className={classes.iconPass}
                                name="icon-password"
                              />
                            ) : (
                              <Icon
                                className={classes.iconPass}
                                name="icon-password-disabled"
                              />
                            )}
                          </div>
                        ),
                      }}
                      helperText={
                        passwordRequired ? t('validations:requiredField') : ''
                      }
                      inputProps={{
                        autocomplete: 'new-password',
                      }}
                      onChange={(e) => {
                        setPassword(e.target.value);
                      }}
                      type={showActualPass ? 'text' : 'password'}
                      autoComplete="current-password"
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Collapse in={isPassVisible} timeout={400}>
                    <div id={props.id + '-profile-info-passwords'}>
                      <Grid spacing={1} container>
                        <Grid item xs={12}>
                          <Input
                            className={classes.inputProf}
                            validators={
                              password !== '' && password !== undefined
                                ? ['required']
                                : []
                            }
                            label={t('components:ProfileInfo.newPassword')}
                            name={'newPassword'}
                            value={newPassword}
                            disabled={!password}
                            inputProps={{
                              autocomplete: 'new-password',
                            }}
                            InputProps={{
                              endAdornment: (
                                <div
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => setShowNewPass(!showNewPass)}>
                                  {showNewPass && password ? (
                                    <Icon
                                      className={classes.iconPass}
                                      name="icon-password"
                                    />
                                  ) : (
                                    <Icon
                                      className={classes.iconPass}
                                      name="icon-password-disabled"
                                    />
                                  )}
                                </div>
                              ),
                            }}
                            onChange={(e) => {
                              setNewPassword(e.target.value);
                            }}
                            error={insertNewPasswordError}
                            helperText={
                              insertNewPasswordError
                                ? t('validations:requiredField')
                                : ''
                            }
                            type={showNewPass ? 'text' : 'password'}
                            autoComplete="new-password"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Input
                            className={classes.inputProf}
                            validators={
                              newPassword !== '' && newPassword !== undefined
                                ? ['required']
                                : []
                            }
                            label={t('components:ProfileInfo.confirmPassword')}
                            name={'newPassword'}
                            disabled={!password}
                            inputProps={{
                              autocomplete: 'new-password',
                            }}
                            InputProps={{
                              endAdornment: (
                                <div
                                  style={{ cursor: 'pointer' }}
                                  onClick={() =>
                                    setConfirmationPass(!showConfirmationPass)
                                  }>
                                  {showConfirmationPass && password ? (
                                    <Icon
                                      className={classes.iconPass}
                                      name="icon-password"></Icon>
                                  ) : (
                                    <Icon
                                      className={classes.iconPass}
                                      name="icon-password-disabled"></Icon>
                                  )}
                                </div>
                              ),
                            }}
                            value={confirmPassword}
                            error={confirmPasswordError}
                            helperText={
                              confirmPasswordError
                                ? t('validations:passwordNotMatch')
                                : ''
                            }
                            onChange={(e) => {
                              setConfirmPassword(e.target.value);
                            }}
                            type={showConfirmationPass ? 'text' : 'password'}
                            autoComplete="new-password"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          {newPassword && newPassword === password && (
                            <div className={classes.errorPass}>
                              {t('validations:passwordCannotMatch')}
                            </div>
                          )}
                        </Grid>
                      </Grid>
                    </div>
                  </Collapse>
                </Grid>
                {!isPassVisible && (
                  <Grid xs={12} className={classes.changePassContainer}>
                    <span
                      onClick={() => setIsPassVisible((prev) => !prev)}
                      className={classes.changePass}>
                      {t('components:ChangePassword.title')}
                    </span>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={12} className={classes.panelActionsWrapper}>
            <div className={classes.panelActions}>
              <LinkButton
                className={classes.cancelButton}
                text={t('components:ProfileInfo.cancelLabel')}
                onClick={handleCancel}
              />
              <Button
                className={classes.saveButton}
                type="submit"
                size="large"
                next
                disabled={
                  (!confirmPassword && !newPassword && password) ||
                  (!confirmPassword && newPassword && !password) ||
                  (confirmPassword && !newPassword && !password) ||
                  (newPassword && newPassword === password) ||
                  (newPassword && newPassword !== confirmPassword)
                }>
                {t('common:saveChanges')}
              </Button>
            </div>
          </Grid>
        </Grid>
      </ValidatorForm>
      <Alert
        id="profile-info-alert"
        open={showModal}
        onClose={() => setShowModal(false)}
        text={modalText}
        icon={modalIcon}
        title={t('common:success')}
      />
    </div>
  );
};

export default ProfileInfo;
